import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { DcrDashboardSearch } from '../types/dashboard.types';
import {
  toDcrDashboardFilterSearch,
  toDcrDashboardFilterValues,
} from '../utils/dcrDashboardFilters';

const route = getRouteApi('/_auth/dashboards/dcr/');

const normalizeFilterValues = (values: Record<string, string>) =>
  toDcrDashboardFilterValues(toDcrDashboardFilterSearch(values));

export function useDcrDashboardFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toDcrDashboardFilterValues(search), [search]);
  const defaultValues = useMemo(() => getMonthToDateRange(), []);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toDcrDashboardFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): DcrDashboardSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultValues.dateFrom,
      dateTo: search.dateTo ?? defaultValues.dateTo,
    }),
    [search, defaultValues],
  );

  return { ...filters, params };
}
